import type { Table } from 'dexie'
import type { Coordinates, MediaItem } from '@cht/shared'
import { ApiError } from '../api/client.js'
import type { FieldClient } from '../api/field-client.js'
import { backoffMs } from '../sync/backoff.js'

export type MediaSyncState = 'queued' | 'uploading' | 'retrying' | 'synced' | 'failed'

export interface StoredMedia {
  id: string
  propertyId: string
  authorId: string
  kind: 'photo' | 'video'
  mimeType: string
  blob: Blob
  title: string
  description: string
  coordinates?: Coordinates
  capturedAt: number
  createdAt: number
  updatedAt: number
  syncState: MediaSyncState
  attempts: number
  nextAttemptAt: number
  lastError?: string | undefined
  progressPercent?: number
  remote?: MediaItem
}

export type NewMedia = Pick<StoredMedia, 'id' | 'kind' | 'mimeType' | 'blob' | 'title' | 'description' | 'capturedAt'> & {
  coordinates?: Coordinates
}

export class MediaStore {
  private readonly listeners = new Set<() => void>()
  constructor(
    private readonly propertyId: string,
    private readonly authorId: string,
    private readonly table: Table<StoredMedia, string>,
    private readonly api: FieldClient,
    private readonly now = () => Date.now()
  ) {}

  subscribe(listener: () => void): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener) }

  async list(): Promise<StoredMedia[]> {
    return this.table.where('propertyId').equals(this.propertyId).reverse().sortBy('capturedAt')
  }

  async create(input: NewMedia, online: boolean): Promise<StoredMedia> {
    if (input.blob.size === 0) throw new Error('That capture is empty. Record it again.')
    const now = this.now()
    const item: StoredMedia = {
      ...input, propertyId: this.propertyId, authorId: this.authorId,
      createdAt: now, updatedAt: now, syncState: 'queued', attempts: 0, nextAttemptAt: now
    }
    await this.table.add(item)
    this.emit()
    if (online) void this.upload(item.id).catch(() => undefined)
    return item
  }

  async sync(): Promise<void> {
    const now = this.now()
    const pending = (await this.list()).filter((item) =>
      item.syncState === 'queued' || (item.syncState === 'retrying' && item.nextAttemptAt <= now))
    for (const item of pending) await this.upload(item.id)
  }

  async retry(id: string): Promise<StoredMedia> {
    const item = await this.required(id)
    if (item.syncState !== 'retrying' && item.syncState !== 'failed') return item
    await this.table.update(id, { syncState: 'queued', nextAttemptAt: this.now(), progressPercent: 0 }); this.emit()
    return this.upload(id)
  }

  async removeLocal(id: string): Promise<void> {
    const item = await this.table.get(id)
    if (item?.propertyId === this.propertyId) await this.table.delete(id)
  }

  async updateLocalDetails(id: string, title: string, description: string): Promise<void> {
    const item = await this.table.get(id)
    if (item?.propertyId === this.propertyId) await this.table.update(id, { title, description, updatedAt: this.now() })
  }

  private async upload(id: string): Promise<StoredMedia> {
    const item = await this.required(id)
    if (item.syncState === 'synced' || item.syncState === 'uploading') return item
    await this.table.update(id, { syncState: 'uploading', progressPercent: 0, updatedAt: this.now() }); this.emit()
    try {
      await this.api.uploadMedia(item, (progressPercent) => { void this.table.update(id, { progressPercent, updatedAt: this.now() }).then(() => this.emit()) })
      await this.table.update(id, { syncState: 'synced', progressPercent: 100, updatedAt: this.now(), nextAttemptAt: Number.MAX_SAFE_INTEGER, lastError: undefined })
    } catch (error) {
      const permanent = error instanceof ApiError && error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status)
      const attempts = item.attempts + 1
      await this.table.update(id, {
        syncState: permanent ? 'failed' : 'retrying', attempts,
        nextAttemptAt: permanent ? Number.MAX_SAFE_INTEGER : this.now() + backoffMs(item.attempts),
        lastError: readableError(error), updatedAt: this.now()
      })
    }
    this.emit()
    return this.required(id)
  }

  private async required(id: string): Promise<StoredMedia> {
    const item = await this.table.get(id)
    if (!item || item.propertyId !== this.propertyId) throw new Error('Media item not found')
    return item
  }

  private emit(): void { for (const listener of this.listeners) listener() }
}

function readableError(error: unknown): string {
  if (error instanceof ApiError) {
    try { return (JSON.parse(error.message) as { error?: string }).error ?? `Upload failed (${error.status})` }
    catch { return `Upload failed (${error.status})` }
  }
  return error instanceof Error ? error.message : 'Upload failed'
}
